import { ReactNode, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { css, keyframes } from 'styled-components'
import { Profile } from './api'
import { Cover } from './covers'
import { AvatarImg, GLOW, short } from './avatar'
import { OfficialTick, TeamChip } from './PersonTicks'
import { isBadge } from './badges'

/* ───────────────────────── icons: one stroke, one weight ───────────────────────── */
const paths: Record<string, ReactNode> = {
  edit: <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3zM13.5 6.5l3 3" />,
  share: <path d="M12 4v11M8 8l4-4 4 4M5 13v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />,
  copy: <path d="M9 9h10v11H9zM5 15V4h10" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  info: <path d="M12 11v6M12 7.5v.5M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z" />,
  more: <path d="M5 12h.01M12 12h.01M19 12h.01" strokeWidth={3} />,
  send: <path d="M4 12l16-8-5 16-3-6-8-2z" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  link: <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />,
  external: <path d="M14 4h6v6M20 4l-9 9M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />,
  users: <path d="M16 19v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V19M9.5 10.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM21 19v-1.5a4 4 0 0 0-3-3.9M15 3.6a3.5 3.5 0 0 1 0 6.8" />,
  flag: <path d="M5 21V4M5 4h11l-2 4 2 4H5" />,
  badge: <path d="M12 3l2.6 1.9 3.2-.2 1 3.1 2.6 1.9-1.4 2.9 1.4 2.9-2.6 1.9-1 3.1-3.2-.2L12 22l-2.6-1.9-3.2.2-1-3.1L2.6 15.3 4 12.4 2.6 9.5l2.6-1.9 1-3.1 3.2.2z" />,
  gem: <path d="M6 3h12l4 6-10 12L2 9zM2 9h20M9 3l3 18M15 3l-3 18" />,
  star: <path d="M12 3l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.4l-5.7 3.1 1.2-6.4L2.8 9.7l6.4-.8z" />,
  trophy: <path d="M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8 21h8M9 17h6" />,
  megaphone: <path d="M4 10v4a1 1 0 0 0 1 1h3l8 4V5L8 9H5a1 1 0 0 0-1 1zM8 15l1 5h3l-1-5M19 10a3 3 0 0 1 0 4" />,
  heart: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />,
  lock: <path d="M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6z" />,
  wallet: <path d="M3 7a2 2 0 0 1 2-2h13v4M3 7v11a2 2 0 0 0 2 2h15v-9H3zM16 14h.01" />,
  logout: <path d="M10 4H5v16h5M14 8l4 4-4 4M18 12H9" />,
  image: <path d="M4 5h16v14H4zM4 16l5-5 4 4 3-3 4 4M15 9h.01" />,
  signin: <path d="M14 4h5v16h-5M10 8l4 4-4 4M14 12H4" />,
}
export const ACH_ICON: Record<string, string> = { joined: 'flag', profile_complete: 'badge', og_profile: 'gem', level2_avatar: 'star', competition_winner: 'trophy', competition_host: 'megaphone', followed_10: 'heart' }
export function Icon({ name, size = 20 }: { name: string; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false" style={{ flex: 'none' }}>
      {paths[name] ?? paths.info}
    </svg>
  )
}

/* ───────────────────────── identity card ───────────────────────── */
const Shell = styled.section`
  position: relative;
  border-radius: 28px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid rgba(127, 212, 232, 0.14);
  box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.02) inset;
  isolation: isolate;
`
const CoverWrap = styled.div`
  position: relative;
  [data-testid='profile-cover'] {
    border-radius: 0;
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(8, 12, 30, 0) 40%, rgba(8, 12, 30, 0.55) 78%, ${({ theme }) => theme.colors.backgroundAlt} 100%);
    pointer-events: none;
  }
`
const Body = styled.div`
  position: relative;
  padding: 0 16px 18px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 0 28px 24px;
  }
`
const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-top: -52px;
  ${({ theme }) => theme.mediaQueries.md} {
    margin-top: -64px;
  }
`
const AvatarSlot = styled.div`
  flex: none;
  border-radius: 50%;
  padding: 4px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  filter: drop-shadow(0 10px 24px rgba(0, 0, 0, 0.45));
  .lg {
    display: none;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    .sm {
      display: none;
    }
    .lg {
      display: block;
    }
  }
`
const Actions = styled.div`
  display: flex;
  gap: 8px;
  padding-bottom: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`
export const ActionBtn = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 160ms;
  border: 1px solid ${({ $primary }) => ($primary ? 'transparent' : 'rgba(127, 212, 232, 0.35)')};
  color: ${({ $primary, theme }) => ($primary ? '#0b1220' : theme.colors.text)};
  background: ${({ $primary }) => ($primary ? 'linear-gradient(135deg, #8fe3f5 0%, #7fd4e8 35%, #9a7bff 100%)' : 'rgba(13, 21, 48, 0.72)')};
  box-shadow: ${({ $primary }) => ($primary ? '0 10px 26px -8px rgba(127, 212, 232, 0.65), 0 1px 0 rgba(255,255,255,0.35) inset' : '0 6px 18px -10px rgba(0,0,0,0.6)')};
  backdrop-filter: blur(8px);
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${GLOW};
    outline-offset: 2px;
  }
`
const Name = styled.h1`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  .nm {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
  }
  .nm > span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  margin: 14px 0 0;
  font-size: clamp(26px, 7vw, 32px);
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.colors.text};
  overflow-wrap: anywhere;
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 40px;
  }
`
const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin-top: 8px;
  a,
  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    padding: 0 10px;
    border-radius: 999px;
    font: inherit;
    font-size: 14px;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text};
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    text-decoration: none;
    font-variant-numeric: tabular-nums;
  }
  a:hover,
  button:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`
export const LevelBadge = styled.span<{ $lvl?: number | null }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 800;
  color: #0b1220;
  background: ${({ $lvl }) => ($lvl === 2 ? 'linear-gradient(135deg, #d8c4ff, #b38bff)' : 'linear-gradient(135deg, #b9f0ff, #7fd4e8)')};
  box-shadow: ${({ $lvl }) => ($lvl === 2 ? '0 6px 18px -6px rgba(179, 139, 255, 0.8)' : '0 6px 18px -6px rgba(127, 212, 232, 0.8)')};
`
const Counts = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 14px;
  button {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-height: 44px;
    padding: 0 12px 0 0;
    background: none;
    border: 0;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text};
    font: inherit;
  }
  strong {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
  }
  span {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  button[aria-pressed='true'] span {
    color: #8fe3f5;
  }
  button:focus-visible {
    outline: 2px solid ${GLOW};
    outline-offset: 2px;
    border-radius: 8px;
  }
`

export function useShare(url: string, title: string) {
  const [done, setDone] = useState(false)
  return {
    done,
    share: async () => {
      try {
        if (typeof navigator !== 'undefined' && navigator.share) {
          await navigator.share({ title, url })
          return
        }
        await navigator.clipboard?.writeText(url)
        setDone(true)
        setTimeout(() => setDone(false), 1800)
      } catch {
        /* cancelled */
      }
    },
  }
}

export function IdentityCard({
  p,
  actions,
  onCounts,
  openList,
  children,
}: {
  p: Profile
  actions?: ReactNode
  onCounts?: (which: 'followers' | 'following') => void
  openList?: 'followers' | 'following' | null
  children?: ReactNode
}) {
  const off = p.state === 'off' && !p.is_me
  // 2026-10-07: a wallet that never signed has an automatic page (its public activity) but no profile yet: no counts, an "Unclaimed" tag
  const auto = p.state === 'none'
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(p.address)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* no clipboard */
    }
  }
  return (
    <Shell data-testid="identity-card">
      <CoverWrap>
        <Cover id={p.cover ?? undefined} level={p.avatar?.level} h={off ? 120 : 220} />
      </CoverWrap>
      <Body>
        <TopRow>
          <AvatarSlot>
            <span className="sm">
              <AvatarImg avatar={p.avatar} size={96} off={off} />
            </span>
            <span className="lg">
              <AvatarImg avatar={p.avatar} size={128} off={off} />
            </span>
          </AvatarSlot>
          {actions && <Actions>{actions}</Actions>}
        </TopRow>
        <Name data-testid="profile-name">
          {/* the gold tick stays glued to the name; the team chips wrap on their own when the line is full */}
          <span className="nm">
            <span data-testid="profile-handle">{p.handle && !off ? `@${p.handle}` : short(p.address)}</span>
            {p.state === 'on' && p.official && <OfficialTick size={24} box={34} scale />}
          </span>
          {p.state === 'on' &&
            (p.teams ?? [])
              .filter(isBadge)
              .slice(0, 2)
              .map((b, i) => <TeamChip key={`${b.token}:${b.role}`} badge={b} size={22} h={30} shield={!p.official && i === 0} />)}
        </Name>
        <Meta>
          <a href={`https://bscscan.com/address/${p.address}`} target="_blank" rel="noreferrer" title={`${p.address} on BscScan`} data-testid="bscscan-link">
            <Icon name="wallet" size={16} />
            {short(p.address)}
          </a>
          <button type="button" onClick={copy} aria-label="Copy address">
            <Icon name={copied ? 'check' : 'copy'} size={16} />
            {copied ? 'Copied' : 'Copy'}
          </button>
          {auto ? (
            <LevelBadge as="span" data-testid="profile-tag-unclaimed" title="This wallet has not claimed its CADINU profile. Everything shown is public BNB Chain activity." style={{ background: 'rgba(255, 210, 122, 0.14)', color: '#ffd27a', boxShadow: 'inset 0 0 0 1px rgba(255, 210, 122, 0.45)' }}>
              <Icon name="flag" size={15} />
              Unclaimed
            </LevelBadge>
          ) : off ? (
            <span style={{ fontSize: 14, color: '#c9c3dd' }}>Profile off</span>
          ) : (
            <>
              {p.avatar?.level ? (
                <LevelBadge $lvl={p.avatar.level}>
                  <Icon name={p.avatar.level === 2 ? 'star' : 'badge'} size={15} />
                  Level {p.avatar.level}
                </LevelBadge>
              ) : (
                <LevelBadge as="span" style={{ background: 'rgba(255,255,255,0.08)', color: '#c9c3dd', boxShadow: 'none' }}>
                  No level yet
                </LevelBadge>
              )}
              {p.joined && <span style={{ fontSize: 14, color: 'rgba(201,195,221,0.8)' }}>since {new Date(p.joined).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>}
            </>
          )}
        </Meta>
        {!off && !auto && (
          <Counts>
            <button type="button" onClick={() => onCounts?.('followers')} aria-pressed={openList === 'followers'}>
              <strong data-testid="followers-count">{p.followers ?? 0}</strong>
              <span>Followers</span>
            </button>
            <button type="button" onClick={() => onCounts?.('following')} aria-pressed={openList === 'following'}>
              <strong>{p.following ?? 0}</strong>
              <span>Following</span>
            </button>
          </Counts>
        )}
        {children}
      </Body>
    </Shell>
  )
}

/* ───────────────────────── tabs ───────────────────────── */
export type TabId = 'achievements' | 'nfts' | 'rewards' | 'earn'
export type TabItem = { id: TabId; label: string; short: string }
export const TABS: TabItem[] = [
  { id: 'achievements', label: 'Achievements', short: 'Badges' },
  { id: 'nfts', label: 'NFTs', short: 'NFTs' },
  { id: 'rewards', label: 'Rewards & Referral', short: 'Rewards' },
]
/** your own profile only (2026-10-09): the affiliate programme, /u#earn */
export const MY_TABS: TabItem[] = [...TABS, { id: 'earn', label: 'Earn', short: 'Earn' }]
const TabList = styled.div<{ $n: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $n }) => $n}, minmax(0, 1fr));
  gap: 4px;
  padding: 4px;
  border-radius: 18px;
  background: rgba(13, 21, 48, 0.8);
  border: 1px solid rgba(127, 212, 232, 0.12);
  box-shadow: 0 10px 30px -18px rgba(0, 0, 0, 0.7);
  position: sticky;
  top: 60px;
  z-index: 5;
  backdrop-filter: blur(10px);
  button {
    min-height: 46px;
    border: 0;
    border-radius: 14px;
    font: inherit;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textSubtle};
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: color 160ms, background 160ms, box-shadow 160ms;
    font-variant-numeric: tabular-nums;
  }
  button[aria-selected='true'] {
    color: #0b1220;
    background: linear-gradient(135deg, #8fe3f5, #7fd4e8 40%, #9a7bff);
    box-shadow: 0 8px 22px -8px rgba(127, 212, 232, 0.7), 0 1px 0 rgba(255, 255, 255, 0.35) inset;
  }
  button:focus-visible {
    outline: 2px solid ${GLOW};
    outline-offset: -2px;
  }
  button small {
    font-size: 12px;
    font-weight: 700;
    opacity: 0.75;
  }
  .long {
    display: none;
  }
  /* four tabs keep the short words until there is room for "Rewards & Referral" */
  ${({ theme, $n }) => ($n > 3 ? theme.mediaQueries.md : theme.mediaQueries.sm)} {
    .long {
      display: inline;
    }
    .shortl {
      display: none;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    button {
      transition: none;
    }
  }
`
export function useTab(defaultTab: TabId = 'achievements', items: TabItem[] = TABS) {
  const [tab, setTabState] = useState<TabId>(defaultTab)
  const ids = items.map((t) => t.id).join(',')
  useEffect(() => {
    const h = window.location.hash.replace('#', '') as TabId
    if (ids.split(',').includes(h)) setTabState(h)
  }, [ids])
  const setTab = (t: TabId) => {
    setTabState(t)
    try {
      window.history.replaceState(null, '', `#${t}`)
    } catch {
      /* ignore */
    }
  }
  return [tab, setTab] as const
}
export function Tabs({
  tab,
  setTab,
  counts,
  publicView,
  items = TABS,
}: {
  tab: TabId
  setTab: (t: TabId) => void
  counts?: Partial<Record<TabId, number | string>>
  publicView?: boolean
  items?: TabItem[]
}) {
  const onKey = (e: React.KeyboardEvent) => {
    const i = items.findIndex((t) => t.id === tab)
    if (e.key === 'ArrowRight') setTab(items[(i + 1) % items.length].id)
    if (e.key === 'ArrowLeft') setTab(items[(i + items.length - 1) % items.length].id)
  }
  return (
    <TabList role="tablist" aria-label="Profile sections" onKeyDown={onKey} data-testid="profile-tabs" $n={items.length}>
      {items.map((t) => (
        <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={tab === t.id} aria-controls={`panel-${t.id}`} tabIndex={tab === t.id ? 0 : -1} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}>
          <span className="long">{publicView && t.id === 'rewards' ? 'Rewards' : t.label}</span>
          <span className="shortl">{t.short}</span>
          {counts?.[t.id] !== undefined && <small>{counts[t.id]}</small>}
        </button>
      ))}
    </TabList>
  )
}
const panelIn = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`
export const Panel = styled.div`
  display: grid;
  gap: 14px;
  animation: ${panelIn} 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
export function TabPanel({ id, tab, children }: { id: TabId; tab: TabId; children: ReactNode }) {
  if (tab !== id) return null
  return (
    <Panel role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} data-testid={`panel-${id}`}>
      {children}
    </Panel>
  )
}

/* ───────────────────────── (i) hint ───────────────────────── */
const HintBtn = styled.button`
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 0;
  background: rgba(255, 255, 255, 0.06);
  color: ${({ theme }) => theme.colors.textSubtle};
  cursor: pointer;
  vertical-align: middle;
  &[aria-expanded='true'] {
    color: #8fe3f5;
    background: rgba(127, 212, 232, 0.14);
  }
  &:focus-visible {
    outline: 2px solid ${GLOW};
  }
`
const HintBody = styled.p`
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text};
  background: rgba(127, 212, 232, 0.08);
  border: 1px solid rgba(127, 212, 232, 0.18);
`
/** A heading row with an (i) that reveals the explanation on tap; the paragraph never sits in the layout by default. */
export function Hint({ label, text, children }: { label: string; text: ReactNode; children?: ReactNode }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {children}
        <HintBtn type="button" aria-label={`About ${label}`} aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
          <Icon name="info" size={16} />
        </HintBtn>
      </div>
      {open && <HintBody id={id}>{text}</HintBody>}
    </div>
  )
}

/* ───────────────────────── drawer ───────────────────────── */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 120;
  background: rgba(4, 7, 20, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  ${({ theme }) => theme.mediaQueries.md} {
    align-items: stretch;
    justify-content: flex-end;
  }
`
const sheetIn = keyframes`from { transform: translateY(24px); opacity: 0; } to { transform: none; opacity: 1; }`
const Sheet = styled.div`
  width: 100%;
  max-height: 92dvh;
  overflow: auto;
  overscroll-behavior: contain;
  border-radius: 24px 24px 0 0;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid rgba(127, 212, 232, 0.16);
  box-shadow: 0 -20px 60px -20px rgba(0, 0, 0, 0.7);
  animation: ${sheetIn} 240ms cubic-bezier(0.2, 0.8, 0.2, 1);
  display: grid;
  grid-template-rows: auto 1fr;
  scrollbar-width: thin;
  scrollbar-color: rgba(127, 212, 232, 0.35) transparent;
  ${({ theme }) => theme.mediaQueries.md} {
    width: min(480px, 100%);
    max-height: none;
    height: 100dvh;
    border-radius: 24px 0 0 24px;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
const SheetHead = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.01em;
  }
  button {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    border: 0;
    background: rgba(255, 255, 255, 0.06);
    color: inherit;
    cursor: pointer;
    display: grid;
    place-items: center;
  }
`
export function Drawer({ open, onClose, title, children, testId = 'edit-drawer' }: { open: boolean; onClose: () => void; title: string; children: ReactNode; testId?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    ref.current?.querySelector<HTMLElement>('button, input, [tabindex]')?.focus()
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])
  if (!open || typeof document === 'undefined') return null
  return createPortal(
    <Backdrop onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid="edit-backdrop">
      <Sheet role="dialog" aria-modal="true" aria-label={title} ref={ref} data-testid={testId}>
        <SheetHead>
          <h2>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </SheetHead>
        <div style={{ padding: '4px 20px 32px', display: 'grid', gap: 22, alignContent: 'start' }}>{children}</div>
      </Sheet>
    </Backdrop>,
    document.body,
  )
}

/* ───────────────────────── shared panel surfaces ───────────────────────── */
export const Surface = styled.section<{ $lit?: boolean }>`
  border-radius: 22px;
  padding: 18px;
  min-width: 0;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid rgba(127, 212, 232, 0.1);
  box-shadow: 0 16px 40px -24px rgba(0, 0, 0, 0.7);
  ${({ $lit }) =>
    $lit &&
    css`
      background: radial-gradient(120% 100% at 0% 0%, rgba(127, 212, 232, 0.14), transparent 55%), radial-gradient(100% 90% at 100% 100%, rgba(154, 123, 255, 0.16), transparent 55%), ${({ theme }) => theme.colors.backgroundAlt};
      border-color: rgba(127, 212, 232, 0.22);
    `}
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 22px;
  }
`
export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  gap: 8px;
`
export const BigNumber = styled.div`
  font-size: clamp(28px, 8vw, 36px);
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text};
  small {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0;
    margin-left: 6px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
`
export const Note = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
`
